import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import useSEO from "@/lib/useSEO";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const addrLines = (a) => [a?.name, `${a?.street || ""} ${a?.number || ""}`.trim(), `${a?.zip || ""} ${a?.city || ""} ${a?.province || ""}`.trim(), a?.country].filter(Boolean);

export default function OrderConfirm() {
  useSEO({ title: "Conferma ordine", description: "Il tuo ordine su Giovanna Matino Interior." });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [waiting, setWaiting] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const cart = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setNotFound(true); setWaiting(false); return; }
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      if (cancelled) return;
      count += 1;
      try {
        const { data } = await api.get(`/orders/confirmation/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setOrder(data); setWaiting(false);
          if (!clearedRef.current) { cart.clear(); clearedRef.current = true; }
          return;
        }
      } catch { /* ordine non ancora confermato */ }
      if (count >= 15) { setWaiting(false); setNotFound(true); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (waiting) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="order-waiting">
        <div className="text-center">
          <Loader2 size={36} className="mx-auto animate-spin text-gold" />
          <p className="mt-5 text-taupe text-sm">Stiamo verificando il pagamento con il circuito…</p>
        </div>
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="order-pending">
        <div className="max-w-lg text-center bg-white border border-hair p-10">
          <X size={26} className="mx-auto text-taupe" />
          <h1 className="font-serif text-3xl mt-4">Non abbiamo ancora conferma del pagamento.</h1>
          <p className="text-taupe mt-4 text-sm">Se hai completato il pagamento, riceverai a breve l'email di conferma dell'ordine. Per assistenza contattaci.</p>
          <Link to="/shop" className="btn-outline-ink mt-8 inline-flex">Torna allo Shop</Link>
        </div>
      </div>
    );
  }

  const isService = order.kind === "stp_service";

  return (
    <div className="min-h-[70vh] bg-bone px-6 py-16 flex items-start justify-center" data-testid="page-order-confirm">
      <div className="max-w-2xl w-full bg-white border border-hair p-8 md:p-12">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto">
            <Check size={30} className="text-gold" strokeWidth={2} />
          </div>
          <div className="eyebrow mt-6">{isService ? "Servizio attivato" : "Ordine confermato"}</div>
          <h1 className="font-serif text-4xl mt-3">Grazie{order.customer?.name ? `, ${order.customer.name}` : ""}!</h1>
          <p className="text-taupe mt-3 text-sm">{isService ? "Riferimento" : "Numero ordine"}: <strong data-testid="order-number">{order.order_number}</strong></p>
          <p className="text-taupe mt-2 text-sm">{isService
            ? "Il tuo Shop the Project è attivo: lo Studio inizierà la selezione sulla base del tuo brief e ti invierà la Shopping List personalizzata. Riceverai a breve un'email di conferma."
            : "Riceverai a breve un'email di conferma con il riepilogo. La fattura verrà inviata successivamente a questo indirizzo email."}</p>
        </div>

        <div className="mt-8 border-t border-hair pt-6 space-y-3 text-sm" data-testid="order-items">
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between gap-4">
              <span>{it.quantity}× {it.name}{it.variant ? ` — ${it.variant}` : ""}{it.lead_time ? <span className="block text-[10px] text-amber-700 uppercase tracking-wide">Tempistica indicativa: {it.lead_time}</span> : null}</span>
              <span className="text-taupe shrink-0">{eur(it.price * it.quantity)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-hair pt-3"><span className="text-taupe">Subtotale</span><span>{eur(order.subtotal)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-emerald-700"><span>Sconto</span><span>-{eur(order.discount)}</span></div>}
          {(order.studio_discount || 0) > 0 && <div className="flex justify-between text-gold"><span>Vantaggio Clienti Studio</span><span>-{eur(order.studio_discount)}</span></div>}
          {!isService && <div className="flex justify-between"><span className="text-taupe">Spedizione</span><span>{order.shipping_cost === 0 ? "Gratuita" : eur(order.shipping_cost)}</span></div>}
          <div className="flex justify-between border-t border-hair pt-3 items-baseline"><span className="eyebrow">Totale pagato</span><span className="font-serif text-2xl" data-testid="order-total">{eur(order.amount)}</span></div>
          <p className="text-[11px] text-taupe">Metodo: {order.provider === "paypal" ? "PayPal" : "Carta (Stripe)"}</p>
        </div>

        {!isService && (
        <div className="mt-8 grid sm:grid-cols-2 gap-6 text-sm" data-testid="order-addresses">
          <div>
            <div className="eyebrow !text-[10px] mb-2">Spedizione</div>
            {addrLines(order.shipping).map((l, i) => <p key={i} className="text-taupe">{l}</p>)}
          </div>
          <div>
            <div className="eyebrow !text-[10px] mb-2">Fatturazione ({order.billing_type})</div>
            {order.billing_same
              ? <p className="text-taupe">Come l'indirizzo di spedizione</p>
              : addrLines(order.billing).map((l, i) => <p key={i} className="text-taupe">{l}</p>)}
          </div>
        </div>
        )}

        <div className="mt-10 flex gap-3 justify-center flex-wrap">
          <Link to="/shop" className="btn-outline-ink" data-testid="order-back-shop">Continua lo shopping</Link>
          <Link to="/" className="btn-ink" data-testid="order-go-home">Torna alla home</Link>
        </div>
      </div>
    </div>
  );
}
